import { useMutation } from '@apollo/client';

import createProctorConfigOverrideMutation from 'bundles/author-assess-common/data/proctoring/CreateProctorConfigOverride.graphql';
import proctorConfigOverrideQuery from 'bundles/author-assess-common/data/proctoring/GetProctorConfigOverride.graphql';
import type {
  CreateProctorConfigOverrideMutation as Data,
  CreateProctorConfigOverrideMutationVariables as Variables,
} from 'bundles/author-assess-common/data/proctoring/__generated__/CreateProctorConfigOverrideMutation';
import { convertDGSProctorConfigDataToProctorConfigDraft } from 'bundles/author-assess-common/lib/ConversionUtils';
import { getIdForProctorConfigOverride } from 'bundles/author-assess-common/lib/Utils';
import type { ProctorConfigOverrideDraft } from 'bundles/authoring/common/types/proctorConfig';

export type CreateProctorConfigOverrideInput = {
  maxAttempts?: number;
  maxSubmissionsPerAttempt?: number;
  attemptDuration?: number;
};

export type InputProps = {
  assessmentId?: string;
  userId?: number | string;
};

export type CreateProctorConfigOverrideProps = {
  createProctorConfigOverride?: (
    input: CreateProctorConfigOverrideInput
  ) => Promise<ProctorConfigOverrideDraft | undefined> | undefined;
};

/**
 * This hook provides `createProctorConfigOverride` mutation.
 * It is expected to be used on "Item Setting Exception" page for Peer Review
 * assignment type.
 */
export const useCreateProctorConfigOverride = (params: InputProps): CreateProctorConfigOverrideProps => {
  const { assessmentId, userId } = params;

  const [mutation] = useMutation<Data, Variables>(createProctorConfigOverrideMutation, {
    context: { clientName: 'gatewayGql' },
  });

  if (!assessmentId || !userId) {
    return {};
  }

  const proctorConfigurationId = getIdForProctorConfigOverride({
    assessmentId,
    userId,
  });

  return {
    createProctorConfigOverride: (createProctorConfigOverrideInput: CreateProctorConfigOverrideInput) => {
      return mutation({
        variables: {
          input: {
            assessmentId,
            // userId is a number but typescript and the GraphQL endpoint expect a string
            submitterId: `${userId}`,
            ...createProctorConfigOverrideInput,
          },
        },
        context: { clientName: 'gatewayGql' },
        refetchQueries: [
          {
            query: proctorConfigOverrideQuery,
            context: { clientName: 'gatewayGql' },
            variables: {
              assessmentId,
              submitterId: `${userId}`,
            },
          },
        ],
        notifyOnNetworkStatusChange: true,
      }).then((result) => {
        return convertDGSProctorConfigDataToProctorConfigDraft(
          proctorConfigurationId,
          result?.data?.Proctor_createOverrideForAssignmentSubmitter
        );
      });
    },
  };
};
